<template>
  <component
    :is="computedTag"
    ref="button"
    :type="type"
    :to="to"
    class="ak-btn ak-modal__footer-item"
    :class="classes"
    :disabled="disabled"
    v-bind="attrs"
    @click="clicked"
  >
    <template v-if="label">{{ label }}</template>
    <template v-else-if="$slots.default">
      <slot />
    </template>
  </component>
</template>

<script>
import { ref, defineComponent, inject } from 'vue'
import {
  buttonMixin,
  buttonProps,
  modalButtonProps,
} from '../../mixins/buttonMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-button :ghost=false :type="'secondary'">Click Me</ak-button>
   */

  name: 'AkModalButton',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...buttonProps,
    ...modalButtonProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const modal = inject('akModal')
    const button = ref(null)

    const mixin = buttonMixin({
      button,
      props,
      emit,
    })

    /**
     * @param {Event} e
     * @description Overload the clicked method from the button mixin as the modal
     * buttons have to handle some extra things
     */
    const clicked = (e) => {
      /* istanbul ignore if */
      if (e && e.originalEvent instanceof Event) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (props.close) {
        modal.close()
      }

      emit('click', e)
    }

    return {
      button,
      ...mixin,
      clicked,
    }
  },
})
</script>
